import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../common/Layout";
import { useAuth } from "../../../contexts/AuthContext";
import dashboardService from "../../../services/dashboardService";
import useDashboardNavigation from "../common/useDashboardNavigation";
import SRActionCards from "./SRActionCards";
import SRMyNextActions from "./SRMyNextActions";
import SRStaleLeads from "./SRStaleLeads";
import SRFunnel from "./SRFunnel";
import SRReminderRate from "./SRReminderRate";
import ViewLead from "../../leads/ViewLead";
import { getUserTimezone } from "../../../utils/timezone";

function SRDashboard() {
  const { user } = useAuth();
  const nav = useDashboardNavigation();
  const userTimezone = getUserTimezone();

  const [counts, setCounts] = useState(null);
  const [nextActions, setNextActions] = useState([]);
  const [staleDays, setStaleDays] = useState(30);
  const [staleLeads, setStaleLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [reminder, setReminder] = useState(null);

  const [loading, setLoading] = useState({
    counts: true, next: true, stale: true, funnel: true, reminder: true,
  });
  const [errors, setErrors] = useState({});

  const [viewLeadId, setViewLeadId] = useState(null);

  const loadCounts = useCallback(async () => {
    setLoading((l) => ({ ...l, counts: true }));
    try {
      setCounts(await dashboardService.srActionCounts());
      setErrors((e) => ({ ...e, counts: null }));
    } catch {
      setErrors((e) => ({ ...e, counts: "Failed to load counts" }));
    } finally {
      setLoading((l) => ({ ...l, counts: false }));
    }
  }, []);

  const loadNext = useCallback(async () => {
    setLoading((l) => ({ ...l, next: true }));
    try {
      const d = await dashboardService.srNextActions(5);
      setNextActions(Array.isArray(d?.data) ? d.data : []);
      setErrors((e) => ({ ...e, next: null }));
    } catch {
      setErrors((e) => ({ ...e, next: "Failed to load next actions" }));
    } finally {
      setLoading((l) => ({ ...l, next: false }));
    }
  }, []);

  const loadStale = useCallback(async (days) => {
    setLoading((l) => ({ ...l, stale: true }));
    try {
      const d = await dashboardService.srStaleLeads(days);
      setStaleLeads(Array.isArray(d?.data) ? d.data : []);
      setErrors((e) => ({ ...e, stale: null }));
    } catch {
      setErrors((e) => ({ ...e, stale: "Failed to load stale leads" }));
    } finally {
      setLoading((l) => ({ ...l, stale: false }));
    }
  }, []);

  const loadFunnel = useCallback(async () => {
    setLoading((l) => ({ ...l, funnel: true }));
    try {
      setFunnel(await dashboardService.srFunnel());
      setErrors((e) => ({ ...e, funnel: null }));
    } catch {
      setErrors((e) => ({ ...e, funnel: "Failed to load funnel" }));
    } finally {
      setLoading((l) => ({ ...l, funnel: false }));
    }
  }, []);

  const loadReminder = useCallback(async () => {
    setLoading((l) => ({ ...l, reminder: true }));
    try {
      setReminder(await dashboardService.srReminderRate(30));
      setErrors((e) => ({ ...e, reminder: null }));
    } catch {
      setErrors((e) => ({ ...e, reminder: "Failed to load reminder rate" }));
    } finally {
      setLoading((l) => ({ ...l, reminder: false }));
    }
  }, []);

  useEffect(() => {
    loadCounts();
    loadNext();
    loadStale(staleDays);
    loadFunnel();
    loadReminder();
  }, [loadCounts, loadNext, loadStale, loadFunnel, loadReminder, staleDays]);

  const refreshAll = () => {
    loadCounts();
    loadNext();
    loadStale(staleDays);
    loadFunnel();
    loadReminder();
  };

  const handleCardClick = (key) => {
    const map = {
      dueToday: nav.goDueToday,
      dueThisWeek: nav.goDueThisWeek,
      overdue: nav.goOverdue,
      noNextContact: nav.goNoNext,
    };
    (map[key] || (() => {}))();
  };

  const openLead = (leadId) => setViewLeadId(leadId);
  const closeLead = () => {
    setViewLeadId(null);
    refreshAll();
  };

  const firstName = (user?.fullName || "").split(" ")[0] || "there";

  return (
    <Layout>
      <div className="dashboard-v2">
        <div className="dashboard-v2-header">
          <h1>Welcome back, {firstName}</h1>
          <p className="dashboard-v2-subtitle">
            Everything you need today — due contacts, overdue items, and your pipeline.
          </p>
        </div>

        <SRActionCards
          counts={counts}
          loading={loading.counts}
          error={errors.counts}
          onCardClick={handleCardClick}
        />

        <div className="dashboard-v2-grid-2">
          <SRMyNextActions
            items={nextActions}
            loading={loading.next}
            error={errors.next}
            onOpenLead={openLead}
            onRefresh={loadNext}
            onViewAll={() => nav.goDueToday()}
          />
          <SRStaleLeads
            items={staleLeads}
            days={staleDays}
            onDaysChange={setStaleDays}
            loading={loading.stale}
            error={errors.stale}
            onOpenLead={openLead}
            onViewAll={() => nav.goStale({ staleDays })}
          />
        </div>

        <SRFunnel
          data={funnel}
          loading={loading.funnel}
          error={errors.funnel}
          onStageClick={(status) => nav.goByStatus(status)}
        />

        <SRReminderRate
          data={reminder}
          loading={loading.reminder}
          error={errors.reminder}
          onClick={() => nav.goRecentActivity()}
        />

        <div className="dashboard-timezone-footer">
          <i className="fas fa-clock" />
          All times displayed in your timezone: <strong>{userTimezone}</strong>
        </div>
      </div>

      {viewLeadId && (
        <ViewLead
          leadId={viewLeadId}
          embedded={true}
          onClose={closeLead}
          onChanged={refreshAll}
        />
      )}
    </Layout>
  );
}

export default SRDashboard;